import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ContentText from "../components/contentText.jsx";
import { SiteContentContext } from "../context/SiteContentContext";
import { useLocalePath } from "../hooks/useLocalePath";

/**
 * The page for a URL that is not a page — "/sv/haj".
 *
 * The router used to have nothing under `/:locale` for an unmatched path, so the request fell
 * through to the catch-all that redirects to the stored locale: it navigated to the very URL it
 * was already on and rendered an empty outlet, i.e. a blank page under the header. The server
 * answers 404 for these (server/routes.js) and renders this page into the HTML, so the visitor
 * gets somewhere to click and a crawler gets a real not-found document.
 */
export default function NotFound() {
  const { localePath } = useLocalePath();
  // The same key the <h1> renders, so the tab and the page never say two different things and
  // there is one string to translate rather than two. Helmet takes plain strings only, so this
  // reads the value directly instead of through <ContentText>.
  const { getContent } = useContext(SiteContentContext);

  return (
    <div className="flex flex-col items-center justify-center h-[75vh] p-4 max-w-2xl mx-auto">
      <Helmet>
        <title>{`${getContent("not_found.title", "Sidan finns inte")} | AS Maskiner`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="shadow-lg p-8 w-full text-center" data-testid="page-not-found">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-secondary">
          <ContentText contentKey="not_found.title" fallback="Sidan finns inte" />
        </h1>
        <p className="mb-8 text-text-body">
          <ContentText
            contentKey="not_found.body"
            fallback="Adressen kan ha ändrats, eller så blev det ett stavfel på vägen. Börja om från startsidan, eller titta direkt i maskinkatalogen."
          />
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={localePath("/")}
            className="inline-block bg-primary text-inverse py-2 px-6 rounded-md shadow-md hover:bg-primary800 transition-colors duration-300"
          >
            <ContentText contentKey="not_found.cta_home" fallback="Till startsidan" />
          </Link>
          <Link
            to={localePath("/catalogue")}
            className="inline-block border border-primary text-primary py-2 px-6 rounded-md hover:bg-gray-50 transition-colors duration-300"
          >
            <ContentText contentKey="not_found.cta_catalogue" fallback="Till maskinkatalogen" />
          </Link>
        </div>
      </div>
    </div>
  );
}
